import { Trans, useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import { Text } from 'designSystem/components/Text'
import type {
  InspectionPreviewResult,
  InspectionViewContext,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/inspectionResultSchema'
import {
  inspectionComponentId,
  inspectionProvenance,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/inspectionResultSchema'
import { GlassButtonWell } from 'features/knowledgeDocumentViewer/components/viewer/shared/glassSurface'
import { X } from 'lucide-react'
import { lazy, Suspense } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import styled from 'styled-components'

import { InspectionCalculationDetails } from './DocChatInspectionCard'
import { ReportView } from './ReportView'

const Model = lazy(() => import('./DocChatInspectionViewer'))
const Root = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
`
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem 0.75rem;
  gap: 1rem;
`
const Content = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 0.5rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

export function InspectionPreviewPanel({
  result,
  onClose,
  onViewContextChange,
}: {
  result: InspectionPreviewResult
  onClose: () => void
  onViewContextChange?: (context: InspectionViewContext | null) => void
}) {
  const { t } = useLingui()
  if (result.kind === 'report')
    return (
      <Root>
        <Header>
          <Text weight='medium'>
            {result.presentation.title || result.title}
          </Text>
          <Button
            variant='ghost'
            size='icon'
            onClick={onClose}
            aria-label={t`Close preview`}>
            <X size={16} />
          </Button>
        </Header>
        <Content>
          {result.presentation.summary ? (
            <Text as='p' color='muted'>
              {result.presentation.summary}
            </Text>
          ) : null}
          <ReportView
            report={result.report}
            onViewContextChange={onViewContextChange}
          />
        </Content>
      </Root>
    )
  const source =
    result.kind === 'nii_assessment' ? undefined : inspectionProvenance(result)
  const componentId =
    result.kind === 'nii_assessment' ? undefined : inspectionComponentId(result)
  const title =
    result.presentation?.title ||
    (result.kind === 'nii_assessment'
      ? result.title
      : [result.unit_label, result.component_label]
          .filter(Boolean)
          .join(' · ')) ||
    t`Inspection`
  return (
    <Root>
      <Header>
        <div>
          <Text weight='medium'>{title}</Text>
          <Text as='p' variant='caption' color='muted'>
            {result.kind === 'nii_assessment'
              ? result.unit_label
              : source?.inspection_date?.slice(0, 10)}
          </Text>
        </div>
        <GlassButtonWell>
          <Button
            variant='ghost'
            size='icon'
            onClick={onClose}
            aria-label={t`Close preview`}>
            <X size={16} />
          </Button>
        </GlassButtonWell>
      </Header>
      <Content>
        {result.kind !== 'nii_assessment' && result.calculation_error ? (
          <Text as='p' variant='caption'>
            {result.calculation_error}
          </Text>
        ) : null}
        {result.presentation?.summary ? (
          <Text as='p' variant='body' color='muted'>
            {result.presentation.summary}
          </Text>
        ) : null}
        {result.report ? (
          <ReportView
            report={result.report}
            onViewContextChange={onViewContextChange}
          />
        ) : result.kind === 'nii_assessment' ? (
          <Text as='p' color='muted'>
            <Trans>
              Ask Cantilever to reopen this assessment to generate a report.
            </Trans>
          </Text>
        ) : (
          <>
            <ErrorBoundary
              fallback={
                <Text>
                  <Trans>The inspection model is unavailable.</Trans>
                </Text>
              }>
              <Suspense
                fallback={
                  <Text>
                    <Trans>Loading inspection model…</Trans>
                  </Text>
                }>
                {source?.asset_model_id &&
                source.inspection_id &&
                source.dataset_id &&
                componentId &&
                result.unit_id &&
                result.site_id ? (
                  <Model
                    result={result}
                    onViewContextChange={onViewContextChange}
                    assetModelId={source.asset_model_id}
                    inspectionId={source.inspection_id}
                    datasetId={source.dataset_id}
                    componentId={componentId}
                    unitId={result.unit_id}
                    siteId={result.site_id}
                  />
                ) : (
                  <Text>
                    <Trans>The inspection model is unavailable.</Trans>
                  </Text>
                )}
              </Suspense>
            </ErrorBoundary>
            {result.kind === 'remaining_life' ? (
              <InspectionCalculationDetails result={result} />
            ) : null}
          </>
        )}
      </Content>
    </Root>
  )
}
